import { useCallback, useState, useMemo } from 'react'
import { useNotify } from 'shared/ui/Notify'
import { makeKiddomGetRequest, makeKiddomPostRequest } from 'infrastructure/api'
import { makeContentServiceGetRequest } from 'infrastructure/contentService'
import {
  EdGateProfileResp,
  ApiStandardsStatesResp,
  ApiSubjectsResp,
  State,
  Subject,
  StandardChangelistParams,
  KiddomAddStandard,
  KiddomUpdateStandard,
  KiddomStandard,
} from './types'
import { exportStandardsToSpreadsheet } from './utility'
import { googleUserAtom } from 'infrastructure/googleDrive'
import { useSetAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'

// Check if an error is an authentication error
function isAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  const errorMessage = error.message.toLowerCase()
  return (
    errorMessage.includes('invalid authentication') ||
    errorMessage.includes('invalid_token') ||
    errorMessage.includes('token expired') ||
    errorMessage.includes('authentication credential') ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('401')
  )
}

// Generic hook for debounced error notifications
function useDebouncedNotify(debounceMs = 5000) {
  const [lastErrorTime, setLastErrorTime] = useState(0)
  const notify = useNotify()

  const notifyWithDebounce = useCallback(
    (message: string) => {
      const now = Date.now()
      if (now - lastErrorTime > debounceMs) {
        notify(message, { type: 'error' })
        setLastErrorTime(now)
      }
    },
    [notify, lastErrorTime, debounceMs],
  )

  return notifyWithDebounce
}

// Hook for fetching EdGate profile data
export function useEdGateProfile() {
  const [profile, setProfile] = useState<EdGateProfileResp | null>(null)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const notify = useNotify()

  const fetchProfile = useCallback(async () => {
    setLoadingProfile(true)
    const response = await makeContentServiceGetRequest(
      '/content/v1/standards/edgate/profile',
    )
    const result = await response.json()

    if (!response.ok) {
      notify(
        `Failed to load EdGate profile: HTTP ${response.status}: ${response.statusText}`,
        { type: 'error' },
      )
      setProfile(null)
      setLoadingProfile(false)
      return null
    }

    setProfile(result)
    setLoadingProfile(false)
    return result
  }, [notify])

  return { profile, loadingProfile, fetchProfile }
}

// Hook for fetching EdGate standards
export function useEdGateStandards() {
  const [loadingEdgateStandards, setLoadingEdgateStandards] = useState(false)
  const notify = useNotify()

  const fetchEdgateStandards = useCallback(
    async (state: string, subject: string) => {
      if (!state || !subject) {
        return null
      }

      setLoadingEdgateStandards(true)
      const response = await makeContentServiceGetRequest(
        `/content/v1/standards/edgate/cache?state=${encodeURIComponent(state)}&subject=${encodeURIComponent(subject)}`,
      )
      const result = await response.json()

      if (!response.ok) {
        notify(
          `Failed to load EdGate standards: HTTP ${response.status}: ${response.statusText}`,
          { type: 'error' },
        )
        setLoadingEdgateStandards(false)
        return null
      }

      setLoadingEdgateStandards(false)
      return result
    },
    [notify],
  )

  return {
    loadingEdgateStandards,
    fetchEdgateStandards,
  }
}

// Hook for fetching Kiddom standards
export function useKiddomStandards() {
  const [loadingKiddomStandards, setLoadingKiddomStandards] = useState(false)
  const notifyWithDebounce = useDebouncedNotify()

  const fetchKiddomStandards = useCallback(
    async (state: string, subject: string) => {
      if (!state || !subject) {
        return null
      }

      setLoadingKiddomStandards(true)
      const response = await makeContentServiceGetRequest(
        `/content/v1/standards/kiddom?state=${encodeURIComponent(state)}&subject=${encodeURIComponent(subject)}`,
      )
      const result = await response.json()

      if (!response.ok) {
        notifyWithDebounce(
          `Failed to load Kiddom standards: HTTP ${response.status}: ${response.statusText}`,
        )
        setLoadingKiddomStandards(false)
        return null
      }

      setLoadingKiddomStandards(false)
      return result
    },
    [notifyWithDebounce],
  )

  return {
    loadingKiddomStandards,
    fetchKiddomStandards,
  }
}

// Hook for fetching changelist
export function useChangelist() {
  const [loadingChangelist, setLoadingChangelist] = useState(false)
  const notify = useNotify()

  const fetchChangelist = useCallback(
    async (params: StandardChangelistParams) => {
      setLoadingChangelist(true)
      const queryParams = new URLSearchParams({
        state: params.state,
        kiddom_subject: params.kiddom_subject,
        edgate_subject: params.edgate_subject,
        trace_parents: params.trace_parents?.toString() || 'false',
      })

      const response = await makeContentServiceGetRequest(
        `/content/v1/standards/changelist?${queryParams.toString()}`,
      )
      const result = await response.json()

      if (!response.ok) {
        notify(
          `Failed to load changelist: HTTP ${response.status}: ${response.statusText}`,
          { type: 'error' },
        )
        setLoadingChangelist(false)
        return null
      }

      setLoadingChangelist(false)
      return result
    },
    [notify],
  )

  return { loadingChangelist, fetchChangelist }
}

// Hook for fetching states
export function useStates() {
  const [states, setStates] = useState<State[] | null>(null)
  const [loadingStates, setLoadingStates] = useState(false)
  const notifyWithDebounce = useDebouncedNotify()

  const fetchStates = useCallback(async () => {
    setLoadingStates(true)
    const { result, error } =
      await makeKiddomGetRequest<ApiStandardsStatesResp>({
        kiddomPath: '/api/v2/standards/states',
      })

    if (error) {
      notifyWithDebounce(`Failed to load states: ${error}`)
      setLoadingStates(false)
      return
    }

    if (!result) {
      notifyWithDebounce('No states data received from server')
      setLoadingStates(false)
      return
    }

    const data = result
      .map((state) => ({
        name: state.abbreviation,
        desc: state.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

    setStates(data)
    setLoadingStates(false)
  }, [notifyWithDebounce])

  return { states, loadingStates, fetchStates }
}

// Hook for fetching subjects
export function useSubjects() {
  const [subjects, setSubjects] = useState<Subject[] | null>(null)
  const [loadingSubjects, setLoadingSubjects] = useState(false)
  const notifyWithDebounce = useDebouncedNotify()

  const fetchSubjects = useCallback(async () => {
    setLoadingSubjects(true)
    const { result, error } = await makeKiddomGetRequest<ApiSubjectsResp>({
      kiddomPath: '/api/subjects',
    })

    if (error) {
      notifyWithDebounce(`Failed to load subjects: ${error}`)
      setLoadingSubjects(false)
      return
    }

    if (!result?.subjects) {
      notifyWithDebounce('No subjects data received from server')
      setLoadingSubjects(false)
      return
    }

    const data = result.subjects
      .map((subject) => ({
        subject_id: subject.subject_id,
        subject_name: subject.subject_name,
      }))
      .sort((a, b) => a.subject_name.localeCompare(b.subject_name))

    setSubjects(data)
    setLoadingSubjects(false)
  }, [notifyWithDebounce])

  return { subjects, loadingSubjects, fetchSubjects }
}

// Hook for adding standards
export function useAddStandards() {
  const [loadingAddStandards, setLoadingAddStandards] = useState(false)
  const notify = useNotify()

  const addStandards = useCallback(
    async (standards: KiddomAddStandard[]) => {
      setLoadingAddStandards(true)
      const { error } = await makeKiddomPostRequest<void>({
        kiddomPath: '/api/v2/standards/add',
        requestBody: { standards },
      })

      if (error) {
        notify(`Failed to add standards: ${error}`, { type: 'error' })
        setLoadingAddStandards(false)
        return false
      }

      notify('Standards added successfully', { type: 'success' })
      setLoadingAddStandards(false)
      return true
    },
    [notify],
  )

  return { addStandards, loadingAddStandards }
}

// Hook for updating standards
export function useUpdateStandards() {
  const [loadingUpdateStandards, setLoadingUpdateStandards] = useState(false)
  const notify = useNotify()

  const updateStandards = useCallback(
    async (standards: KiddomUpdateStandard[]) => {
      setLoadingUpdateStandards(true)
      const { error } = await makeKiddomPostRequest<void>({
        kiddomPath: '/api/v2/standards/update',
        requestBody: { standards },
      })

      if (error) {
        notify(`Failed to update standards: ${error}`, { type: 'error' })
        setLoadingUpdateStandards(false)
        return false
      }

      notify('Standards updated successfully', { type: 'success' })
      setLoadingUpdateStandards(false)
      return true
    },
    [notify],
  )

  return { updateStandards, loadingUpdateStandards }
}

// Hook for exporting standards to Google Sheet
export function useExportStandardsToSheet() {
  const [loadingExport, setLoadingExport] = useState(false)
  const notify = useNotify()
  const resetGoogleAuth = useSetAtom(googleUserAtom)
  const guser = useAtomValue(googleUserAtom)

  const canExport = !!guser.credential?.accessToken

  const exportToSheet = useCallback(
    async (standards: KiddomStandard[], state: string, subjectName: string) => {
      if (!canExport) {
        notify('Google authentication required', { type: 'info' })
        return null
      }

      if (!standards || standards.length === 0) {
        notify('No standards to export', { type: 'info' })
        return null
      }

      const accessToken = guser.credential?.accessToken
      if (!accessToken) {
        notify('Google authentication required', { type: 'info' })
        return null
      }
      setLoadingExport(true)
      try {
        const spreadsheetId = await exportStandardsToSpreadsheet(
          standards,
          state,
          subjectName,
          accessToken,
        )
        notify('Standards exported to Google Sheet successfully', {
          type: 'success',
        })
        setLoadingExport(false)
        return spreadsheetId
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to export standards'

        // Check if this is an authentication error
        if (isAuthError(error)) {
          // Reset auth state to force re-login
          resetGoogleAuth()
          notify('Google authentication expired. Please log in again.', {
            type: 'error',
          })
        } else {
          notify(`Failed to export standards: ${errorMessage}`, {
            type: 'error',
          })
        }
        setLoadingExport(false)
        return null
      }
    },
    [notify, resetGoogleAuth, canExport, guser],
  )

  return useMemo(
    () => ({
      exportToSheet,
      loadingExport,
      canExport,
    }),
    [exportToSheet, loadingExport, canExport],
  )
}
